<template>
  <div>
    <div :class="$style.container">
      <AppPopover v-show="forceValidate && isFocused" :class="$style.popover">
        <ul :class="$style['popover--invalid']" v-show="isInvalid">
          <li
            v-for="message in validationMessages"
            :key="message"
            :class="$style.popover__message"
          >
            <IconExclamationCircle :class="$style.popover__icon" />
            <div>{{ message }}</div>
          </li>
        </ul>
        <ul :class="$style['popover--valid']" v-show="!isInvalid">
          <li :class="$style.popover__message">
            <IconCheckCircle :class="$style.popover__icon" />
            <div>有効なパスワードです</div>
          </li>
        </ul>
      </AppPopover>

      <div :class="$style.wrapper">
        <DolphinTextInput
          :type="isPasswordVisible ? 'text' : 'password'"
          :class="$style.wrapper__input"
          v-model="value"
          @focus="isFocused = true"
          @blur="isFocused = false"
        />
        <button
          :class="$style.button"
          type="button"
          aria-label=""
          @click="toggleShowing"
        >
          <component
            :is="isPasswordVisible ? 'icon-eye-off' : 'icon-eye'"
            :class="[
              $style.button__eye,
              $style[
                `button__eye--${isPasswordVisible ? 'active' : 'inactive'}`
              ],
            ]"
          >
          </component>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { validatePassword } from '@/features/password/passwordValidators'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle.vue'
import IconEye from '@/components/icons/IconEye.vue'
import IconEyeOff from '@/components/icons/IconEyeOff.vue'
import AppPopover from '@/components/atoms/AppPopover.vue'

export type ValidateChangeEventParams = {
  isValid: boolean
  errorMessages: string[]
}

export default defineComponent({
  components: {
    DolphinTextInput,
    IconCheckCircle,
    IconExclamationCircle,
    IconEye,
    IconEyeOff,
    AppPopover,
  },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    forceValidate: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    'update:modelValue': null,
    validateChange: (value: ValidateChangeEventParams) => value !== undefined,
  },
  setup(props, { emit }) {
    const isPasswordVisible = ref(false)
    const isFocused = ref(false)

    const validationMessages = computed(() =>
      validatePassword(props.modelValue)
    )
    const isInvalid = computed(
      () => props.forceValidate && validationMessages.value.length > 0
    )

    const onValueChange = (value: string): void => {
      emit('update:modelValue', value)
      emit('validateChange', {
        isValid: !isInvalid.value,
        errorMessages: validationMessages.value,
      } as ValidateChangeEventParams)
    }

    const value = computed({
      get: () => props.modelValue,
      set: (value: string): void => {
        emit('update:modelValue', value)
      },
    })

    const toggleShowing = (): void => {
      isPasswordVisible.value = !isPasswordVisible.value
    }

    watch(() => props.modelValue, onValueChange)

    return {
      value,
      toggleShowing,
      isPasswordVisible,
      isInvalid,
      isFocused,
      validationMessages,
    }
  },
})
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  padding: 10px 16px 11px;
  border: 1px solid $color-border-low-emphasis;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-base;

  &__input {
    flex-grow: 1;
    padding: 0;
    border: 0;
  }
}

.button {
  &__eye {
    width: 28px;
    height: auto;
    color: $color-text-and-object-medium-emphasis;

    &--active {
      color: $color-text-and-object-high-emphasis;
    }

    &--inactive {
      color: $color-text-and-object-low-emphasis;
    }
  }
}

.popover {
  position: absolute;
  transform: translateY(-100%);

  &--invalid {
    color: $color-caution-accent;
  }

  &--valid {
    color: $color-success-object;
  }

  &__message {
    display: flex;
    align-items: center;
  }

  &__icon {
    margin-right: 0.5em;
  }
}
</style>
